import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Form, Button, Modal } from 'antd';
// Api
import {
  CREATE_CAMPAIGN_SUBMISSION_FILES,
  SUBMIT_CAMPAIGN_OFFER_RESULTS,
} from 'api/campaignV2/mutations';
// Types
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_campaign_activities_SocialActivity,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  CampaignOfferStatus,
  CampaignOfferSubmissionInput,
  CampaignTaskType,
  SubmitCampaignOfferResultsInput,
} from 'api/graphql-global-types';
import {
  CreateCampaignSubmissionFiles,
  CreateCampaignSubmissionFilesVariables,
} from 'api/campaignV2/types/CreateCampaignSubmissionFiles';
import {
  SubmitCampaignOfferResults,
  SubmitCampaignOfferResultsVariables,
} from 'api/campaignV2/types/SubmitCampaignOfferResults';
// Helpers
import { uploadDeliverables } from 'helpers/campaign';
import {
  EMPTY_SOCIAL_STATE,
  getInitialSocialState,
  getTaskSubmissions,
  SocialStateEnum,
  SocialStateType,
} from 'helpers/deliverables';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import SocialInputField from 'components/common/SocialInputField/SocialInputField';
// Styles
import styles from './SocialDeliverables.module.scss';

type SocialDeliverablesV2Props = {
  record: GetCampaignOffers_getCampaignOffers_entities | null;
  onClose: () => void;
  onFinish: () => void;
};

const SocialDeliverablesV2 = ({
  record,
  onClose,
  onFinish,
}: SocialDeliverablesV2Props): JSX.Element => {
  const [form] = Form.useForm();
  const campaignActivity = record?.campaign
    .activities?.[0] as GetCampaignOffers_getCampaignOffers_entities_campaign_activities_SocialActivity;

  const tasks = campaignActivity?.tasks || [];
  const postTypes = tasks?.map((task) => task.type);

  const isIGPost = postTypes?.includes(CampaignTaskType.IG_POST) || false;
  const isIGReel = postTypes?.includes(CampaignTaskType.IG_REEL) || false;
  const isIGStory = postTypes?.includes(CampaignTaskType.IG_STORY) || false;
  const isIGLive = postTypes?.includes(CampaignTaskType.IG_LIVE) || false;
  const isTikTok = postTypes?.includes(CampaignTaskType.TT_VIDEO) || false;
  const isTwitterTweet =
    postTypes?.includes(CampaignTaskType.TW_TWEET) || false;
  const isTwitterVideo =
    postTypes?.includes(CampaignTaskType.TW_VIDEO) || false;
  const isTwitterLive = postTypes?.includes(CampaignTaskType.TW_LIVE) || false;

  const [socialState, setSocialState] =
    useState<SocialStateType>(EMPTY_SOCIAL_STATE);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [createCampaignSubmissionFiles] = useMutation<
    CreateCampaignSubmissionFiles,
    CreateCampaignSubmissionFilesVariables
  >(CREATE_CAMPAIGN_SUBMISSION_FILES);

  const [submitCampaignOffer] = useMutation<
    SubmitCampaignOfferResults,
    SubmitCampaignOfferResultsVariables
  >(SUBMIT_CAMPAIGN_OFFER_RESULTS);

  const handleFinish = () => {
    onFinish();
    handleReset();
  };

  const handleReset = () => {
    onClose();
    form.resetFields();
    setIsLoading(false);
    setSocialState(EMPTY_SOCIAL_STATE);
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const input: SubmitCampaignOfferResultsInput = {
        offerId: record?.id || '',
        ownerId: record?.userId,
        submissions: [],
      };

      const allSubmissions: CampaignOfferSubmissionInput[] = [];

      // Process each task's submissions
      for (const task of tasks) {
        const taskSubmissions = getTaskSubmissions(task, socialState);
        if (!taskSubmissions) {
          continue;
        }

        if (task.type === CampaignTaskType.CONTENT) {
          // Handle content task files
          if (taskSubmissions.files && taskSubmissions.files.length > 0) {
            const { data } = await createCampaignSubmissionFiles({
              variables: {
                input: {
                  offerId: record?.id || '',
                  files: taskSubmissions.files.map((file: any) => ({
                    name: file.name,
                  })),
                },
              },
            });

            if (data?.createCampaignSubmissionFiles.length) {
              const uploadedUrls = await uploadDeliverables(
                data.createCampaignSubmissionFiles,
                taskSubmissions.files
              );

              uploadedUrls.forEach((url: any) => {
                allSubmissions.push({
                  type: CampaignTaskType.CONTENT,
                  imageKey: url.key,
                  taskId: task.id,
                });
              });
            }
          }
        } else if (
          task.type === CampaignTaskType.IG_LIVE ||
          task.type === CampaignTaskType.IG_STORY
        ) {
          // Handle IgLive and IgStory tasks - only files needed
          if (taskSubmissions.files && taskSubmissions.files.length > 0) {
            const { data } = await createCampaignSubmissionFiles({
              variables: {
                input: {
                  offerId: record?.id || '',
                  files: taskSubmissions.files.map((file: any) => ({
                    name: file.name,
                  })),
                },
              },
            });

            if (data?.createCampaignSubmissionFiles.length) {
              const uploadedUrls = await uploadDeliverables(
                data.createCampaignSubmissionFiles,
                taskSubmissions.files
              );

              uploadedUrls.forEach((url: any) => {
                allSubmissions.push({
                  type: task.type,
                  imageKey: url.key,
                  taskId: task.id,
                });
              });
            }
          }
        } else {
          // Handle link-based tasks (all other types: IG Post, IG Reel, TT Video, TW Tweet, TW Video, TW Live)
          if (taskSubmissions.postCopy && taskSubmissions.postCopy.trim()) {
            allSubmissions.push({
              type: task.type,
              link: taskSubmissions.postCopy,
              taskId: task.id,
            });
          }
        }
      }

      input.submissions = [...allSubmissions];

      await submitCampaignOffer({
        variables: {
          input,
        },
      });
      successNotification(`Campaign deliverables added successfully`);

      handleFinish();
    } catch (err) {
      errorNotification((err as Error)?.message || 'Something went wrong');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (record) {
      const initialSocialState = getInitialSocialState(record);
      setSocialState(initialSocialState);
    } else {
      setSocialState(EMPTY_SOCIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  // disable submit if any of the inputs is missing a value or has an empty string(in case of links)
  const disableSubmit =
    (isIGPost && !!socialState.iGPosts.filter((item) => item === '').length) ||
    (isIGReel && !!socialState.iGReels.filter((item) => item === '').length) ||
    (isTikTok && !!socialState.tikToks.filter((item) => item === '').length) ||
    (isTwitterTweet &&
      !!socialState.twitterTweets.filter((item) => item === '').length) ||
    (isTwitterVideo &&
      !!socialState.twitterVideos.filter((item) => item === '').length) ||
    (isTwitterLive &&
      !!socialState.twitterLives.filter((item) => item === '').length) ||
    (isIGLive && !socialState.liveImages.length) ||
    (isIGStory && !socialState.storyImages.length);

  const isCompletedOrPublished =
    record?.statusV2 === CampaignOfferStatus.COMPLETED ||
    record?.statusV2 === CampaignOfferStatus.PUBLISHED;

  return (
    <Modal
      title="Deliverables"
      visible={!!record}
      footer={null}
      onCancel={handleReset}
      width="90vw"
    >
      <Form
        className={styles.root}
        name="createEditSocialCampaignDeliverables"
        aria-label="Create/Edit Social Campaign Deliverables"
        form={form}
        onFinish={handleSubmit}
      >
        {isIGPost && (
          <SocialInputField
            name={SocialStateEnum.iGPosts}
            label="Link to the Instagram post:"
            placeholder="https://instagram.com/hey..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isIGReel && (
          <SocialInputField
            name={SocialStateEnum.iGReels}
            label="Link to the Instagram reel:"
            placeholder="https://instagram.com/hey..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isTikTok && (
          <SocialInputField
            name={SocialStateEnum.tikToks}
            label="Link to the TikTok post:"
            placeholder="Paste here..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isTwitterTweet && (
          <SocialInputField
            name={SocialStateEnum.twitterTweets}
            label="Link to the X tweet:"
            placeholder="Paste here..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isTwitterVideo && (
          <SocialInputField
            name={SocialStateEnum.twitterVideos}
            label="Link to the X video:"
            placeholder="Paste here..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isTwitterLive && (
          <SocialInputField
            name={SocialStateEnum.twitterLives}
            label="Link to the X live:"
            placeholder="Paste here..."
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isIGStory && (
          <SocialInputField
            name={SocialStateEnum.storyImages}
            label="Screenshots of Instagram story and insights:"
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {isIGLive && (
          <SocialInputField
            name={SocialStateEnum.liveImages}
            label="Screenshots of Instagram live and insights:"
            isCompletedOrPublished={isCompletedOrPublished}
            socialState={socialState}
            setState={setSocialState}
          />
        )}
        {!isCompletedOrPublished && (
          <div className={styles.submitButton}>
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading}
              disabled={isLoading || disableSubmit}
            >
              Save
            </Button>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default SocialDeliverablesV2;
